import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Chip,
  Select,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ClinicDialog } from '../components/dialogs/ClinicDialog';
import ConfirmDeleteDialog from '../components/dialogs/ConfirmDeleteDialog';
import { CLINIC_DIALOG_SX } from '../constants/popupStyles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  AddCircleOutline as AddRowIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  patientNotificationService,
  PatientNotification,
  PatientNotificationCreate,
  NotificationEntity,
  NOTIFICATION_ENTITY_LABELS,
  CriteriaItem,
} from '../services/patientNotificationService';
import emailTemplateService, { EmailTemplate } from '../services/emailTemplateService';
import smsTemplateService, { SmsTemplate } from '../services/smsTemplateService';
import { meetingTypeService } from '../services/meetingTypeService';
import { questionnaireService } from '../services/questionnaireService';
import { userService } from '../services/userService';
import { clinicService } from '../services/clinicService';
import CriteriaRow from '../components/criteria/CriteriaRow';
import { ENTITY_FIELD_META } from '../config/fieldMetadata';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import RequirePermission from '../components/common/RequirePermission';
import { useCrudHandlers } from '../hooks/useCrudHandlers';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { patientNotificationColumns } from '../config/patientNotificationColumns';
import patientStatusConfigService from '../services/patientStatusConfigService';
import { usePatientFieldConfigs } from '../hooks/usePatientFieldConfigs';
import { getApiError } from '../utils/apiError';


interface FormValues extends PatientNotificationCreate {
  include_criteria_fields: CriteriaItem[];
  exclude_criteria_fields: CriteriaItem[];
  start_from?: string | null;
}

const emptyCriteriaItem = (): CriteriaItem => ({ field: '', operator: 'equals', value: '' });

const PatientNotificationsPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const { dynamicFieldMeta } = usePatientFieldConfigs();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState<PatientNotification | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState<PatientNotification | null>(null);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [smsTemplates, setSmsTemplates] = useState<SmsTemplate[]>([]);
  const [sendReminders, setSendReminders] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, { id: number | string; name: string }[]>>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      entity: NotificationEntity.PATIENT,
      is_active: true,
      include_criteria_fields: [],
      exclude_criteria_fields: [],
      send_reminders: false,
    },
  });

  const {
    fields: includeFields,
    append: appendInclude,
    remove: removeInclude,
  } = useFieldArray({ control, name: 'include_criteria_fields' });

  const {
    fields: excludeFields,
    append: appendExclude,
    remove: removeExclude,
  } = useFieldArray({ control, name: 'exclude_criteria_fields' });

  const watchedEntity = watch('entity') as NotificationEntity;
  const watchedSendReminders = watch('send_reminders');

  useEffect(() => {
    setSendReminders(!!watchedSendReminders);
  }, [watchedSendReminders]);

  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const [emails, sms] = await Promise.all([
          emailTemplateService.getTemplates(),
          smsTemplateService.getTemplates(),
        ]);
        setEmailTemplates(emails);
        setSmsTemplates(sms);
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    };
    loadTemplates();
  }, []);

  // Load dynamic options for criteria dropdowns when dialog opens
  useEffect(() => {
    if (!dialogOpen || !currentTenant) return;
    const loadDynamicOptions = async () => {
      try {
        const [meetingTypes, questionnaireTypes, users, patientStatuses, clinics] = await Promise.all([
          meetingTypeService.getMeetingTypes(currentTenant.id),
          questionnaireService.getQuestionnaireTypes(currentTenant.id),
          userService.getUsers(currentTenant.id),
          patientStatusConfigService.getAll(currentTenant.id),
          clinicService.getClinics(currentTenant.id),
        ]);
        setDynamicOptions({
          meetingTypes: meetingTypes.map((t) => ({ id: t.id, name: t.name })),
          questionnaireTypes: questionnaireTypes.map((t) => ({ id: t.id, name: t.name })),
          users: users.map((u) => ({ id: u.id, name: u.full_name })),
          patientStatuses: patientStatuses.map((s) => ({ id: s.key, name: s.title })),
          clinics: clinics.map((c) => ({ id: c.id, name: c.name })),
        });
      } catch (err) {
        console.error('Failed to load dynamic criteria options:', err);
      }
    };
    loadDynamicOptions();
  }, [dialogOpen, currentTenant]);

  const getSortValue = useCallback((notification: PatientNotification, key: string): any => {
    switch (key) {
      case 'name': return notification.name.toLowerCase();
      case 'entity': return notification.entity.toLowerCase();
      case 'schedule': return notification.schedule ?? '';
      case 'isActive': return notification.is_active ? 1 : 0;
      case 'sendReminders': return notification.send_reminders ? 1 : 0;
      case 'reminderCount': return notification.reminder_count ?? 0;
      case 'daysBetween': return notification.days_between_reminders ?? 0;
      default: return '';
    }
  }, []);

  const getSearchText = useCallback((notification: PatientNotification): string[] => {
    return [
      notification.name,
      notification.entity,
      String(notification.reminder_count ?? ''),
    ];
  }, []);

  const table = useTablePage<PatientNotification>({
    entityType: 'patientNotifications',
    columns: patientNotificationColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  const loadNotifications = useCallback(async () => {
    if (!currentTenant) return;
    table.loadData(() => patientNotificationService.getAll(currentTenant.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant]);

  useEffect(() => {
    if (currentTenant) {
      loadNotifications();
    }
  }, [currentTenant, loadNotifications]);

  const handleOpenDialog = (notification?: PatientNotification) => {
    if (notification) {
      setEditingNotification(notification);
      setValue('name', notification.name);
      setValue('entity', notification.entity);
      setValue('is_active', notification.is_active ?? true);
      setValue('email_template_id', notification.email_template_id ?? undefined);
      setValue('sms_template_id', notification.sms_template_id ?? undefined);
      setValue('send_reminders', notification.send_reminders);
      setValue('reminder_count', notification.reminder_count ?? undefined);
      setValue('days_between_reminders', notification.days_between_reminders ?? undefined);
      setValue('reminder_email_template_id', notification.reminder_email_template_id ?? undefined);
      setValue('reminder_sms_template_id', notification.reminder_sms_template_id ?? undefined);
      setValue('start_from', notification.start_from ?? undefined);
      setValue('schedule', notification.schedule ?? undefined);
      setValue('include_criteria_fields', notification.include_criteria ?? []);
      setValue('exclude_criteria_fields', notification.exclude_criteria ?? []);
      setSendReminders(notification.send_reminders);
    } else {
      setEditingNotification(null);
      reset({
        entity: NotificationEntity.PATIENT,
        is_active: true,
        include_criteria_fields: [],
        exclude_criteria_fields: [],
        send_reminders: false,
      });
      setSendReminders(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingNotification(null);
    reset();
  };

  const crud = useCrudHandlers<PatientNotification>({
    table,
    createFn: (data) => patientNotificationService.create({ ...data, tenant_id: currentTenant!.id }),
    updateFn: (id, data) => patientNotificationService.update(id as number, data),
    deleteFn: (id) => patientNotificationService.delete(id as number),
    entityName: 'Patient notification',
    onSuccess: () => { handleCloseDialog(); },
    onDeleteSuccess: () => { setDeleteDialogOpen(false); setNotificationToDelete(null); },
    reloadData: loadNotifications,
  });

  const onSubmit = (formData: FormValues) => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }

    const payload: PatientNotificationCreate = {
      name: formData.name,
      entity: formData.entity,
      is_active: formData.is_active ?? true,
      include_criteria: formData.include_criteria_fields?.filter(c => c.field) ?? [],
      exclude_criteria: formData.exclude_criteria_fields?.filter(c => c.field) ?? [],
      email_template_id: formData.email_template_id || null,
      sms_template_id: formData.sms_template_id || null,
      send_reminders: formData.send_reminders ?? false,
      reminder_count: formData.send_reminders ? (formData.reminder_count || null) : null,
      days_between_reminders: formData.send_reminders ? (formData.days_between_reminders || null) : null,
      reminder_email_template_id: formData.send_reminders ? (formData.reminder_email_template_id || null) : null,
      reminder_sms_template_id: formData.send_reminders ? (formData.reminder_sms_template_id || null) : null,
      start_from: formData.start_from || null,
      schedule: formData.schedule || null,
      tenant_id: currentTenant.id,
    };

    crud.handleCreateOrUpdate(payload, editingNotification);
  };

  const handleDelete = () => {
    crud.handleDelete(notificationToDelete);
  };

  const getEmailTemplateName = (id: number | null): string => {
    if (!id) return '—';
    const tmpl = emailTemplates.find((t) => t.id === id);
    return tmpl ? tmpl.name : `#${id}`;
  };

  const getSmsTemplateName = (id: number | null): string => {
    if (!id) return '—';
    const tmpl = smsTemplates.find((t) => t.id === id);
    return tmpl ? tmpl.name : `#${id}`;
  };

  const SCHEDULE_LABELS: Record<string, string> = {
    every_hour: 'Every Hour',
    working_days_10_14_18: 'Working Days 10:00, 14:00, 18:00',
    working_days_8_20: 'Working Days 8:00–20:00',
    every_day_10_14_18: 'Every Day 10:00, 14:00, 18:00',
    every_day_8_20: 'Every Day 8:00–20:00',
  };

  const getColumnValue = (notification: PatientNotification, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'name':
        return (
          <Box display="flex" alignItems="center">
            <NotificationsIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="medium">{notification.name}</Typography>
          </Box>
        );
      case 'entity':
        return (
          <Chip
            label={NOTIFICATION_ENTITY_LABELS[notification.entity as NotificationEntity] ?? notification.entity}
            color="primary"
            size="small"
          />
        );
      case 'emailTemplate':
        return (
          <Typography variant="body2">{getEmailTemplateName(notification.email_template_id)}</Typography>
        );
      case 'smsTemplate':
        return (
          <Typography variant="body2">{getSmsTemplateName(notification.sms_template_id)}</Typography>
        );
      case 'isActive':
        return (
          <Chip
            label={notification.is_active ? 'Active' : 'Inactive'}
            color={notification.is_active ? 'success' : 'default'}
            size="small"
          />
        );
      case 'sendReminders':
        return (
          <Chip
            label={notification.send_reminders ? 'Yes' : 'No'}
            color={notification.send_reminders ? 'success' : 'default'}
            size="small"
          />
        );
      case 'schedule':
        return (
          <Typography variant="body2">
            {notification.schedule ? (SCHEDULE_LABELS[notification.schedule] ?? notification.schedule) : '—'}
          </Typography>
        );
      case 'reminderCount':
        return notification.reminder_count ?? '—';
      case 'daysBetween':
        return notification.days_between_reminders ?? '—';
      case 'includeCriteria':
        return (
          <Typography variant="body2" color="text.secondary">
            {notification.include_criteria?.length
              ? `${notification.include_criteria.length} rule(s)`
              : '—'}
          </Typography>
        );
      case 'excludeCriteria':
        return (
          <Typography variant="body2" color="text.secondary">
            {notification.exclude_criteria?.length
              ? `${notification.exclude_criteria.length} rule(s)`
              : '—'}
          </Typography>
        );
      default:
        return '';
    }
  };

  const handleRunNotification = async (notification: PatientNotification) => {
    try {
      const result = await patientNotificationService.run(notification.id);
      toast.success(`Job queued (ID: ${result.job_id})`);
    } catch (err: any) {
      toast.error(getApiError(err, 'Failed to queue notification job'));
    }
  };

  const getContextMenuOptions = (): ContextMenuOption[] => {
    if (!table.selectedItem) return [];
    return [
      {
        label: 'Run Now',
        icon: <PlayArrowIcon sx={{ mr: 2 }} />,
        onClick: () => {
          handleRunNotification(table.selectedItem!);
          table.handleMenuClose();
        },
      },
      {
        label: 'Edit',
        icon: <EditIcon sx={{ mr: 2 }} />,
        onClick: () => {
          handleOpenDialog(table.selectedItem!);
          table.handleMenuClose();
        },
      },
      {
        label: 'Delete',
        icon: <DeleteIcon sx={{ mr: 2 }} />,
        onClick: () => {
          setNotificationToDelete(table.selectedItem!);
          setDeleteDialogOpen(true);
          table.handleMenuClose();
        },
        color: 'error' as const,
      },
    ];
  };

  const renderMobileCard = (notification: PatientNotification) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} sx={{ pr: 5 }}>
        <Box display="flex" alignItems="center">
          <NotificationsIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
            {notification.name}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Chip
            label={NOTIFICATION_ENTITY_LABELS[notification.entity as NotificationEntity] ?? notification.entity}
            color="primary"
            size="small"
          />
          <Chip
            label={notification.is_active ? 'Active' : 'Inactive'}
            color={notification.is_active ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </Box>
      <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
        {notification.email_template_id && (
          <Typography variant="body2" color="text.secondary">
            Email: {getEmailTemplateName(notification.email_template_id)}
          </Typography>
        )}
        {notification.sms_template_id && (
          <Typography variant="body2" color="text.secondary">
            SMS: {getSmsTemplateName(notification.sms_template_id)}
          </Typography>
        )}
      </Box>
      {notification.send_reminders && (
        <Typography variant="caption" color="text.secondary">
          Reminders: {notification.reminder_count} × every {notification.days_between_reminders} day(s)
        </Typography>
      )}
    </>
  );

  return (
    <RequirePermission check={!!user?.user_privilege?.config} featureName="patient notifications">
      <TablePageLayout loading={table.loading} error={table.error} errorPrefix="Error loading patient notifications:">
        <DataTableToolbar
          {...table.toolbarProps}
          title="Patient Notifications"
          searchPlaceholder="Search notifications by name or entity..."
          actions={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add Notification
            </Button>
          }
        />

        {isMobile ? (
          <MobileCardGrid
            data={table.sortedData}
            renderCard={renderMobileCard}
            getItemKey={(n) => n.id}
            onMenuClick={table.handleRowClick}
            emptyIcon={<NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
            emptyMessage="No patient notifications found"
          />
        ) : (
          <DataTableContainer {...table.containerProps}>
            <DataTableBody
              data={table.sortedData}
              {...table.tableBodyProps}
              getColumnValue={getColumnValue}
              getRowKey={(n) => n.id}
              onRowClick={table.handleRowClick}
            />
          </DataTableContainer>
        )}

        <RowContextMenu {...table.menuProps} options={getContextMenuOptions()} />

        {/* Create/Edit Dialog */}
        <ClinicDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          title={editingNotification ? 'Edit Patient Notification' : 'Create Patient Notification'}
          onSubmit={handleSubmit(onSubmit)}
          maxWidth="md"
          actions={
            <>
              <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton}>
                {editingNotification ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleCloseDialog} sx={CLINIC_DIALOG_SX.cancelButton}>
                Cancel
              </Button>
            </>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Name */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
              <TextField
                {...register('name', { required: 'Name is required' })}
                fullWidth
                size="small"
                error={!!errors.name}
                helperText={errors.name?.message}
                InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
              />
            </Box>

            {/* Active */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Active</Typography>
              <Controller
                name="is_active"
                control={control}
                defaultValue={true}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={field.value ? 'Active' : 'Inactive'}
                  />
                )}
              />
            </Box>

            {/* Entity */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Entity</Typography>
              <Controller
                name="entity"
                control={control}
                defaultValue={NotificationEntity.PATIENT}
                rules={{ required: 'Entity is required' }}
                render={({ field }) => (
                  <Select {...field} fullWidth sx={CLINIC_DIALOG_SX.select}>
                    <MenuItem value={NotificationEntity.PATIENT}>Patient</MenuItem>
                    <MenuItem value={NotificationEntity.QUESTIONNAIRE}>Questionnaire</MenuItem>
                    <MenuItem value={NotificationEntity.MEETING}>Meeting</MenuItem>
                  </Select>
                )}
              />
            </Box>

            {/* Email Template */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Email Template</Typography>
              <Controller
                name="email_template_id"
                control={control}
                defaultValue={undefined}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    sx={CLINIC_DIALOG_SX.select}
                  >
                    <MenuItem value="">None</MenuItem>
                    {emailTemplates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>

            {/* SMS Template */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>SMS Template</Typography>
              <Controller
                name="sms_template_id"
                control={control}
                defaultValue={undefined}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    sx={CLINIC_DIALOG_SX.select}
                  >
                    <MenuItem value="">None</MenuItem>
                    {smsTemplates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Box>

            {/* Start From */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Start From</Typography>
              <Controller
                name="start_from"
                control={control}
                defaultValue={undefined}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    sx={CLINIC_DIALOG_SX.select}
                  >
                    <MenuItem value="">None</MenuItem>
                    {[...(ENTITY_FIELD_META[watchedEntity] ?? []), ...(watchedEntity === NotificationEntity.PATIENT ? dynamicFieldMeta : [])]
                      .filter((f) => f.dataType === 'date')
                      .map((f) => (
                        <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                      ))}
                  </Select>
                )}
              />
            </Box>

            {/* Schedule */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Schedule</Typography>
              <Controller
                name="schedule"
                control={control}
                defaultValue={undefined}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    fullWidth
                    sx={CLINIC_DIALOG_SX.select}
                    displayEmpty
                  >
                    <MenuItem value=""><em>Always (no schedule)</em></MenuItem>
                    <MenuItem value="every_hour">Every Hour (All Days)</MenuItem>
                    <MenuItem value="working_days_10_14_18">Working Days 10:00, 14:00, 18:00</MenuItem>
                    <MenuItem value="working_days_8_20">Working Days 8:00–20:00</MenuItem>
                    <MenuItem value="every_day_10_14_18">Every Day 10:00, 14:00, 18:00</MenuItem>
                    <MenuItem value="every_day_8_20">Every Day 8:00–20:00</MenuItem>
                  </Select>
                )}
              />
            </Box>

            {/* Send Reminders */}
            <Box sx={CLINIC_DIALOG_SX.fieldRow}>
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Reminders</Typography>
              <Controller
                name="send_reminders"
                control={control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Send reminders"
                  />
                )}
              />
            </Box>

            {/* Reminder Count & Days Between (conditional) */}
            {sendReminders && (
              <>
                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Count</Typography>
                  <TextField
                    {...register('reminder_count', {
                      valueAsNumber: true,
                      min: { value: 1, message: 'Must be at least 1' },
                    })}
                    type="number"
                    fullWidth
                    size="small"
                    placeholder="Number of reminders"
                    error={!!errors.reminder_count}
                    helperText={errors.reminder_count?.message}
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                  />
                </Box>

                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Days Between</Typography>
                  <TextField
                    {...register('days_between_reminders', {
                      valueAsNumber: true,
                      min: { value: 1, message: 'Must be at least 1' },
                    })}
                    type="number"
                    fullWidth
                    size="small"
                    placeholder="Days between reminders"
                    error={!!errors.days_between_reminders}
                    helperText={errors.days_between_reminders?.message}
                    InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
                  />
                </Box>

                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Reminder Email</Typography>
                  <Controller
                    name="reminder_email_template_id"
                    control={control}
                    defaultValue={undefined}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ''}
                        fullWidth
                        sx={CLINIC_DIALOG_SX.select}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Same as initial</em></MenuItem>
                        {emailTemplates.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Box>

                <Box sx={CLINIC_DIALOG_SX.fieldRow}>
                  <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Reminder SMS</Typography>
                  <Controller
                    name="reminder_sms_template_id"
                    control={control}
                    defaultValue={undefined}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ''}
                        fullWidth
                        sx={CLINIC_DIALOG_SX.select}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Same as initial</em></MenuItem>
                        {smsTemplates.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Box>
              </>
            )}

            {/* Include Criteria */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" sx={{ color: CLINIC_DIALOG_SX.fieldLabel.color, fontWeight: 500 }}>
                  Include Criteria
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddRowIcon />}
                  onClick={() => appendInclude(emptyCriteriaItem())}
                  sx={{ textTransform: 'none', color: CLINIC_DIALOG_SX.fieldLabel.color }}
                >
                  Add Rule
                </Button>
              </Box>
              {includeFields.map((field, index) => (
                <CriteriaRow
                  key={field.id}
                  index={index}
                  prefix="include_criteria_fields"
                  onRemove={removeInclude}
                  entity={watchedEntity as NotificationEntity || NotificationEntity.PATIENT}
                  control={control}
                  setValue={setValue}
                  dynamicOptions={dynamicOptions}
                  extraFields={(watchedEntity as NotificationEntity) === NotificationEntity.PATIENT ? dynamicFieldMeta : []}
                />
              ))}
              {includeFields.length === 0 && (
                <Typography variant="caption" color="text.secondary">No include rules — all records match.</Typography>
              )}
            </Box>

            {/* Exclude Criteria */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" sx={{ color: CLINIC_DIALOG_SX.fieldLabel.color, fontWeight: 500 }}>
                  Exclude Criteria
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddRowIcon />}
                  onClick={() => appendExclude(emptyCriteriaItem())}
                  sx={{ textTransform: 'none', color: CLINIC_DIALOG_SX.fieldLabel.color }}
                >
                  Add Rule
                </Button>
              </Box>
              {excludeFields.map((field, index) => (
                <CriteriaRow
                  key={field.id}
                  index={index}
                  prefix="exclude_criteria_fields"
                  onRemove={removeExclude}
                  entity={watchedEntity as NotificationEntity || NotificationEntity.PATIENT}
                  control={control}
                  setValue={setValue}
                  dynamicOptions={dynamicOptions}
                  extraFields={(watchedEntity as NotificationEntity) === NotificationEntity.PATIENT ? dynamicFieldMeta : []}
                />
              ))}
              {excludeFields.length === 0 && (
                <Typography variant="caption" color="text.secondary">No exclude rules — no records are excluded.</Typography>
              )}
            </Box>
          </Box>
        </ClinicDialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDelete}
          entityType="Patient Notification"
          entityName={notificationToDelete?.name}
        />

        <ManageColumnsModal {...table.manageColumnsProps} />
        <SaveViewModal {...table.saveViewModalProps} />
      </TablePageLayout>
    </RequirePermission>
  );
};

export default PatientNotificationsPage;
